import React, { useState, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Stack, Typography, Button, IconButton, Alert, Chip, Divider, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { COLORS, PACK_SIZES } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import {
  safeJson, getProp, findHiddenPriceProduct, getPackPriceFromHidden, getVariantInfoFromProducts,
} from "../helpers";
import type { SubscriptionData, MealSelection, PendingChanges, SubIssues } from "../types";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  open: boolean;
  onClose: () => void;
  sub: SubscriptionData | null;
  products: any[];
  recipeProducts: any[];
  getVariantInfo: (variantId: string) => any;
  subIssues?: SubIssues;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
  onRefresh: () => void;
}

export function EditMealsModal({ open, onClose, sub, products, recipeProducts, getVariantInfo, subIssues, onSuccess, onError, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [step, setStep] = useState<"edit" | "review">("edit");
  const [mealEdits, setMealEdits] = useState<Record<string, number>>({});
  const [pending, setPending] = useState<PendingChanges | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initMealEdits = useCallback(() => {
    if (!sub) return;
    const mealsJson = safeJson(getProp(sub, "_paws_meals_json"), []);
    const initial: Record<string, number> = {};
    mealsJson.forEach((m: any) => { if (m.variantId) initial[String(m.variantId)] = m.count || 0; });
    setMealEdits(initial);
    setStep("edit");
    setError(null);
    setPending(null);
  }, [sub]);

  // Re-init when modal opens
  React.useEffect(() => { if (open) initMealEdits(); }, [open, initMealEdits]);

  const getCurrentMeals = (): MealSelection[] => {
    if (!sub) return [];
    const mealsJson = safeJson(getProp(sub, "_paws_meals_json"), []);
    const packSize = parseInt(getProp(sub, "_paws_pack_size_grams") || "400", 10);
    const planDays = parseInt(getProp(sub, "_paws_plan_days") || "7", 10);
    const hiddenProduct = findHiddenPriceProduct(products, planDays);
    const packPrice = getPackPriceFromHidden(hiddenProduct, packSize);

    return mealsJson.map((m: any) => {
      const info = getVariantInfo(m.variantId);
      return {
        variantId: m.variantId,
        count: m.count || 0,
        productTitle: info?.productTitle || m.productTitle || "",
        variantTitle: info?.variantTitle || `${packSize}g`,
        price: packPrice,
      };
    });
  };

  const prepareReview = () => {
    if (!sub) return;
    setError(null);

    const currentMeals = getCurrentMeals();
    const packSize = parseInt(getProp(sub, "_paws_pack_size_grams") || "400", 10);
    const planDays = parseInt(getProp(sub, "_paws_plan_days") || "7", 10);
    const hiddenProduct = findHiddenPriceProduct(products, planDays);
    const packPrice = getPackPriceFromHidden(hiddenProduct, packSize);

    if (!packPrice || packPrice <= 0) {
      setError("Unable to calculate pricing. Please contact support@pawsonplates.co.uk.");
      return;
    }

    const newMeals: MealSelection[] = [];
    for (const [variantId, count] of Object.entries(mealEdits)) {
      if (count > 0) {
        const info = getVariantInfo(variantId);
        if (!info?.productTitle) {
          setError("Could not identify a selected meal. Contact support@pawsonplates.co.uk.");
          return;
        }
        newMeals.push({ variantId, count, productTitle: info.productTitle, variantTitle: info.variantTitle || `${packSize}g`, price: packPrice });
      }
    }

    const same = JSON.stringify(currentMeals.map(m => ({ id: m.variantId, c: m.count })).sort((a, b) => a.id.localeCompare(b.id))) ===
      JSON.stringify(newMeals.map(m => ({ id: m.variantId, c: m.count })).sort((a, b) => a.id.localeCompare(b.id)));
    if (same) { setError("No changes to save"); return; }

    const oldPrice = currentMeals.reduce((s, m) => s + m.price * m.count, 0);
    const newPrice = newMeals.reduce((s, m) => s + m.price * m.count, 0);

    setPending({
      type: "meals",
      oldMeals: currentMeals,
      newMeals,
      oldPrice,
      newPrice,
      mealsSummary: newMeals.filter(m => m.count > 0).map(m => `${m.count}x ${m.productTitle} ${packSize}g`).join(", "),
    });
    setStep("review");
  };

  const handleConfirm = async () => {
    if (!sub || !pending) return;
    setLoading(true);
    setError(null);

    try {
      const currentMeals = safeJson(getProp(sub, "_paws_meals_json"), []);
      const currentCounts: Record<string, number> = {};
      currentMeals.forEach((m: any) => { currentCounts[m.variantId] = m.count || 0; });

      const packSize = parseInt(getProp(sub, "_paws_pack_size_grams") || "400", 10);
      const planDays = parseInt(getProp(sub, "_paws_plan_days") || "7", 10);
      const hiddenProduct = findHiddenPriceProduct(products, planDays);
      const packPrice = getPackPriceFromHidden(hiddenProduct, packSize);

      if (!packPrice || packPrice <= 0) { setError("Unable to calculate pricing."); setLoading(false); return; }

      const lineItems: Array<{ variantId: string; quantity: number; productTitle?: string; variantTitle?: string; price?: number }> = [];

      for (const [variantId, newCount] of Object.entries(mealEdits)) {
        const oldCount = currentCounts[variantId] || 0;
        const delta = newCount - oldCount;
        if (delta !== 0) {
          const info = getVariantInfo(variantId);
          if (delta > 0 && !info?.productTitle) { setError("Could not identify a meal."); setLoading(false); return; }
          lineItems.push({ variantId, quantity: delta, productTitle: info?.productTitle || "", variantTitle: info?.variantTitle || "", price: packPrice });
        }
      }

      for (const [variantId, oldCount] of Object.entries(currentCounts)) {
        if (!(variantId in mealEdits) && oldCount > 0) {
          const info = getVariantInfo(variantId);
          lineItems.push({ variantId, quantity: -oldCount, productTitle: info?.productTitle || "", variantTitle: info?.variantTitle || "", price: packPrice });
        }
      }

      // Send zero-delta lineItems for unchanged meals so the proxy can enrich missing price/title
      for (const [variantId, count] of Object.entries(mealEdits)) {
        if (count > 0 && !lineItems.find(li => li.variantId === variantId)) {
          const info = getVariantInfo(variantId);
          lineItems.push({ variantId, quantity: 0, productTitle: info?.productTitle || "", variantTitle: info?.variantTitle || "", price: packPrice });
        }
      }

      const res = await call("/recharge/subscription/update-meals", {
        method: "POST",
        body: { subscriptionId: sub.id, lineItems },
      });

      if (!res.ok) throw new Error(res.error || "Failed to update meals");

      onSuccess("Meals updated successfully!");
      onClose();
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to update meals");
    } finally {
      setLoading(false);
    }
  };

  const packSize = sub ? parseInt(getProp(sub, "_paws_pack_size_grams") || "400", 10) : 400;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {step === "review" && (
            <IconButton onClick={() => { setStep("edit"); setError(null); }} size="small">
              <ArrowForwardIcon sx={{ transform: "rotate(180deg)" }} />
            </IconButton>
          )}
          <Typography sx={{ fontWeight: 800 }}>{step === "edit" ? "Edit Meals" : "Review Changes"}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === "edit" && subIssues?.hasIssues && (
          <Alert severity="warning" sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Data Issues Detected</Typography>
            {subIssues.issues.map((issue, idx) => (
              <Box key={idx} sx={{ mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: issue.severity === "error" ? COLORS.red : "#FF9800" }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{issue.message}</Typography>
                </Box>
                {issue.details.slice(0, 2).map((detail, dIdx) => (
                  <Typography key={dIdx} sx={{ fontSize: 11, color: COLORS.textMuted, ml: 1.5 }}>• {detail}</Typography>
                ))}

                {issue.type === "mismatch" && (
                  <Box sx={{ display: "flex", gap: 0.5, mt: 1, flexWrap: "wrap", ml: 1.5 }}>
                    <Typography sx={{ fontSize: 10, color: COLORS.textMuted, alignSelf: "center" }}>Remap all to:</Typography>
                    {PACK_SIZES.map(size => (
                      <Button
                        key={size}
                        size="small"
                        variant={size === packSize ? "contained" : "outlined"}
                        disabled={size === packSize}
                        onClick={() => {
                          const mealsJson = safeJson(getProp(sub!, "_paws_meals_json"), []);
                          const newEdits: Record<string, number> = {};
                          for (const meal of mealsJson) {
                            const currentInfo = getVariantInfoFromProducts(String(meal.variantId), products);
                            if (!currentInfo) continue;
                            for (const product of products) {
                              if (product.title !== currentInfo.productTitle) continue;
                              const targetVariant = product.variants?.find((v: any) => {
                                const sm = String(v.title || "").match(/(\d+)/);
                                return sm && parseInt(sm[1], 10) === size;
                              });
                              if (targetVariant) {
                                const vid = String(targetVariant.id);
                                newEdits[vid] = (newEdits[vid] || 0) + (meal.count || 0);
                              }
                              break;
                            }
                          }
                          setMealEdits(newEdits);
                        }}
                        sx={{ fontSize: 10, py: 0.25, px: 1, minWidth: 0, textTransform: "none" }}
                      >
                        {size}g
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Alert>
        )}

        {step === "edit" ? (
          <>
            <Typography sx={{ mb: 2, fontSize: 14, color: COLORS.textMuted }}>
              Select meal quantities for this subscription.
            </Typography>
            {recipeProducts.length === 0 ? (
              <Alert severity="warning">No recipe products found.</Alert>
            ) : (
              <Stack spacing={2}>
                {recipeProducts.map((product: any) => {
                  let matchedVariant = product.variants?.find((v: any) => {
                    const vid = String(v.id);
                    return mealEdits[vid] !== undefined && mealEdits[vid] > 0;
                  });
                  if (!matchedVariant) {
                    matchedVariant = product.variants?.find((v: any) => {
                      const sm = String(v.title || "").match(/(\d+)/);
                      return sm && parseInt(sm[1], 10) === packSize;
                    });
                  }
                  const variant = matchedVariant || product.variants?.[0];
                  if (!variant) return null;
                  const variantId = String(variant.id);
                  const count = mealEdits[variantId] || 0;
                  const vsm = String(variant.title || "").match(/(\d+)/);
                  const displaySize = vsm ? vsm[1] + "g" : variant.title;

                  return (
                    <Box key={product.id} sx={{
                      display: "flex", alignItems: "center", gap: 2, p: 1.5, borderRadius: "8px",
                      bgcolor: count > 0 ? COLORS.orangeLight : QUIZ.color.cream2,
                      border: count > 0 ? `2px solid ${COLORS.orange}` : "1px solid #ddd",
                    }}>
                      {(product.images?.[0]?.src || product.image?.src) && (
                        <Box component="img" src={product.images?.[0]?.src || product.image?.src} alt={product.title}
                          sx={{ width: 50, height: 50, borderRadius: "8px", objectFit: "cover" }} />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{product.title}</Typography>
                        <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}>{displaySize}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconButton size="small" onClick={() => setMealEdits(prev => ({ ...prev, [variantId]: Math.max(0, (prev[variantId] || 0) - 1) }))}><RemoveIcon /></IconButton>
                        <Typography sx={{ fontWeight: 700, minWidth: 24, textAlign: "center" }}>{count}</Typography>
                        <IconButton size="small" onClick={() => setMealEdits(prev => ({ ...prev, [variantId]: (prev[variantId] || 0) + 1 }))}><AddIcon /></IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </>
        ) : (
          <Stack spacing={3}>
            <Alert severity="info">Review the changes below before confirming.</Alert>
            <Box>
              <Typography sx={{ fontWeight: 700, color: COLORS.brown, fontSize: 14, mb: 1 }}>Current Meals</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {pending?.oldMeals?.map((m, i) => <Chip key={i} label={`${m.count}x ${m.productTitle}`} size="small" sx={{ bgcolor: COLORS.redLight }} />)}
                {(!pending?.oldMeals || pending.oldMeals.length === 0) && <Typography sx={{ fontSize: 13, color: COLORS.textMuted }}>No meals</Typography>}
              </Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center" }}><ArrowForwardIcon sx={{ transform: "rotate(90deg)", color: COLORS.brown }} /></Box>
            <Box>
              <Typography sx={{ fontWeight: 700, color: COLORS.green, fontSize: 14, mb: 1 }}>New Meals</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {pending?.newMeals?.map((m, i) => <Chip key={i} label={`${m.count}x ${m.productTitle}`} size="small" sx={{ bgcolor: COLORS.greenLight }} />)}
              </Box>
            </Box>
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ fontWeight: 600 }}>Price Change</Typography>
              <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ fontSize: 14, color: COLORS.textMuted, textDecoration: "line-through" }}>£{pending?.oldPrice?.toFixed(2) || "0.00"}</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: COLORS.green }}>£{pending?.newPrice?.toFixed(2) || "0.00"}</Typography>
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        {step === "edit" ? (
          <Button variant="contained" onClick={prepareReview} sx={{ bgcolor: COLORS.brown, "&:hover": { bgcolor: COLORS.brownDark }, textTransform: "none" }}>
            Review Changes
          </Button>
        ) : (
          <Button variant="contained" onClick={handleConfirm} disabled={loading} sx={{ bgcolor: COLORS.coral, "&:hover": { bgcolor: COLORS.coralDark }, textTransform: "none" }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : "Confirm & Save"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
